/**
 * Security HTTP Interceptor
 * Adds security headers, input validation, and request sanitization
 */

import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, retry } from 'rxjs/operators';
import { ValidationService } from '../services/validation.service';
import { mayRetryRequest } from './http-retry-policy';

@Injectable()
export class SecurityInterceptor implements HttpInterceptor {

  private readonly maxRetries = 2;
  private readonly retryDelay = 1000;

  constructor(private validationService: ValidationService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Special logging for translation files
    if (req.url.includes('/assets/i18n/') || req.url.includes('i18n')) {
      console.log('🌍 Translation file request intercepted:', req.url);
    }

    // Clone the request
    let secureRequest = req;

    // Sanitize request body if present
    if (secureRequest.body && typeof secureRequest.body === 'object') {
      const sanitizedBody = this.sanitizeRequestBody(secureRequest.body);
      secureRequest = secureRequest.clone({ body: sanitizedBody });
    }

    // Validate request URL to prevent SSRF
    if (!this.isValidRequestUrl(secureRequest.url)) {
      console.error('🚫 Request blocked by security interceptor:', secureRequest.url);
      return throwError(() => new Error('Invalid request URL detected'));
    }

    return next.handle(secureRequest).pipe(
      // 🔴 #2156 — plus de rejeu d'un 4xx ni d'un POST : seulement une méthode
      // idempotente, sur une panne passagère (`http-retry-policy.ts`).
      retry({
        count: this.maxRetries,
        delay: (error: unknown) => mayRetryRequest(secureRequest, error)
          ? timer(this.retryDelay)
          : throwError(() => error),
        resetOnSuccess: true
      }),
      catchError((error: HttpErrorResponse) => {
        return this.handleError(error);
      })
    );
  }

  /**
   * Retire le balisage HTML de chaque chaîne du corps.
   *
   * 🔴 #2156 (arbitrage du 29/10) — passait par `sanitizeText`, qui coupait EN
   * SILENCE chaque chaîne à 255 caractères (et la `trim`) : une description ou
   * une adresse longue arrivait tronquée au serveur, sans que personne le sache.
   * La limite de longueur est désormais une validation SERVEUR, avec une erreur
   * claire ; ici on ne retire plus que le balisage.
   */
  private sanitizeRequestBody(body: any): any {
    if (body === null || body === undefined) {
      return body;
    }

    if (typeof body === 'string') {
      return this.validationService.sanitizeHtml(body);
    }

    if (typeof body === 'object') {
      if (Array.isArray(body)) {
        return body.map(item => this.sanitizeRequestBody(item));
      }

      const sanitized: any = {};
      for (const key in body) {
        if (Object.prototype.hasOwnProperty.call(body, key)) {
          sanitized[key] = this.sanitizeRequestBody(body[key]);
        }
      }
      return sanitized;
    }

    return body;
  }

  /**
   * Validate request URL to prevent SSRF attacks
   */
  private isValidRequestUrl(url: string): boolean {
    try {
      console.log('🔍 [Security] Validating URL:', url);
      console.log('🔍 [Security] Window origin:', window.location.origin);

      const parsedUrl = new URL(url, window.location.origin);
      console.log('🔍 [Security] Parsed URL origin:', parsedUrl.origin);
      console.log('🔍 [Security] Parsed URL hostname:', parsedUrl.hostname);

      // Allow requests to assets (i18n files, images, etc.)
      if (url.includes('/assets/') || url.startsWith('/assets/')) {
        console.log('✅ [Security] Allowing request to assets:', url);
        return true;
      }

      // Get hostname early for logging
      const hostname = parsedUrl.hostname.toLowerCase();

      // Allow requests to same origin (relative URLs and same domain)
      if (parsedUrl.origin === window.location.origin) {
        console.log('✅ [Security] Allowing same-origin request:', url, 'Origin:', parsedUrl.origin, 'Window origin:', window.location.origin);
        return true;
      }

      console.log('🔍 [Security] Checking external request:', url, 'Hostname:', hostname);
      
      // Block private IPs for external requests (but allow localhost for development)
      const blockedPatterns = [
        /^10\./,
        /^172\.(1[6-9]|2[0-9]|3[0-1])\./,
        /^192\.168\./,
        /^169\.254\./, // Link-local
        /^::1$/, // IPv6 localhost (except in development)
        /^fc00:/, // IPv6 private
        /^fe80:/ // IPv6 link-local
      ];

      // Only block localhost in production environment
      if (window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1') {
        blockedPatterns.push(/^localhost$/, /^127\./);
      }

      for (const pattern of blockedPatterns) {
        if (pattern.test(hostname)) {
          console.warn('🚫 [Security] Blocked request to private/local address:', hostname);
          console.warn('🔍 [Security] Blocked by pattern:', pattern);
          return false;
        }
      }

      // Allow only HTTPS in production
      if (window.location.protocol === 'https:' && parsedUrl.protocol !== 'https:') {
        console.warn('🚫 [Security] Blocked non-HTTPS request in production:', url);
        console.warn('🔍 [Security] Window protocol:', window.location.protocol);
        console.warn('🔍 [Security] Request protocol:', parsedUrl.protocol);
        return false;
      }

      // Whitelist allowed domains for external requests
      const allowedDomains = [
        'befret.be',
        'dev.befret.be',
        'test.befret.be',
        'befret-development.firebaseapp.com',
        'befret-acceptance.firebaseapp.com',
        'befret-acceptance.web.app',
        'befret-production.firebaseapp.com',
        'us-central1-befret-development.cloudfunctions.net',
        'us-central1-befret-acceptance.cloudfunctions.net',
        'us-central1-befret-production.cloudfunctions.net',
        'europe-west1-befret-development.cloudfunctions.net',
        'europe-west1-befret-acceptance.cloudfunctions.net',
        'europe-west1-befret-production.cloudfunctions.net',
        'api.stripe.com',
        'hooks.stripe.com',
        'js.stripe.com',
        'sendgrid.com',
        'api.sendgrid.com',
        'twilio.com',
        'api.twilio.com',
        // HERE Maps API
        'geocode.search.hereapi.com',
        'autosuggest.search.hereapi.com',
        'discover.search.hereapi.com',
        'lookup.search.hereapi.com',
        // Firebase Functions domains
        'befret-development.cloudfunctions.net',
        'befret-acceptance.cloudfunctions.net',
        'befret-production.cloudfunctions.net',
        // Geolocation services
        'ipapi.co',
        'get.geojs.io',
        'ip-api.com',
        'api.bigdatacloud.net',
        'freegeoip.app',
        'geoiplookup.io',
        'ipinfo.io',
        'localhost', // Allow localhost for development
        '127.0.0.1' // Allow local IP for development
      ];

      const isAllowed = allowedDomains.some(domain =>
        hostname === domain || hostname.endsWith('.' + domain)
      );

      if (!isAllowed) {
        console.warn('🚫 Request to non-whitelisted domain:', hostname);
        console.warn('🔍 Allowed domains:', allowedDomains);
        console.warn('🔍 Full URL:', url);
        console.warn('🔍 Parsed hostname:', hostname);
        return false;
      }

      console.log('✅ [Security] Request to whitelisted domain allowed:', hostname);
      return true;
    } catch (error) {
      console.error('❌ [Security] Error validating URL:', error);
      console.error('❌ [Security] Problematic URL was:', url);
      return false;
    }
  }

  /**
   * Journalise l'erreur et la RENVOIE TELLE QUELLE.
   *
   * 🔴 #2156 — cette méthode remplaçait `error.error` par `{ message }`, une
   * phrase anglaise générique : aucun refus métier du serveur n'atteignait son
   * écran (`TOTAL_BELOW_MINIMUM`, `EMAIL_REQUIRED`, `SHIPMENT_ALREADY_PAID`,
   * `ORDERING_CLOSED`, `CUSTOMS_ITEMS_REQUIRED`, `SHIPPING_OPTION_RETIRED` sur
   * `createInvoice`), alors que `CheckoutService` est écrit pour les laisser
   * remonter. Le message affiché à l'utilisateur est l'affaire de l'écran, qui
   * le choisit d'après le code, dans sa langue.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('HTTP Error:', {
      status: error.status,
      message: error.message,
      url: error.url || undefined,
      timestamp: new Date().toISOString()
    });
    return throwError(() => error);
  }
}